import { BaseWebComponentController, IListenerConfig } from '../../framework/BaseWebComponentController';
import { GzLandingPageAppWebComponent } from '../component/GzLandingPageAppWebComponent';
import { GzLandingPageAppView } from '../view/GzLandingPageAppView';
import { GzLandingPageAppRepository } from '../repository/GzLandingPageAppRepository';
import { template } from '../view/template';
import { showErrorToast } from '@nirvana/shared/common';
import { CardMessage, GzLandingPageCompanyType, LandingPageDependencies } from '../types';
import { WEB_COMPONENT_COMPANIES_APP } from '../../webcomponents.constants';
import { CompaniesAppWebComponent } from '../../companies/companiesApp';
import { CompaniesGridWebComponent } from '../../companies/companiesGrid';
import { GridViewWebComponent } from '../../grid/gridView';
import { ColumnKeys, FILTERS } from '../../companies/companiesGrid/view/gridConfig/companies-grid-columns';
import { MspInProductTrialStatus } from '../../company/types/msp-in-product-trial-status';
import { Observable } from 'rxjs';
import { NavigationHelper } from '@nirvana/shared/navigation';
import Launch from '@repobit/dex-launch';
import Target from '@repobit/dex-target';

export class GzLandingPageAppController extends BaseWebComponentController {
  public component: GzLandingPageAppWebComponent;
  public view!: GzLandingPageAppView;
  public viewConstructor: typeof GzLandingPageAppView;
  public dataRepository: GzLandingPageAppRepository;
  public companyId: string | undefined;
  public companyType: GzLandingPageCompanyType | undefined;
  public gzVersion: string | undefined;
  public messages: CardMessage[] | undefined;
  public rssFeedUrls: string[] | undefined;
  public dynamicEventWidgetInfo: WidgetInfo;
  public dynamicPromoWidgetInfo: WidgetInfo;
  private target: Target;

  constructor(component: GzLandingPageAppWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: GzLandingPageAppView,
      viewTemplate: template,
      dataRepositoryConstructor: GzLandingPageAppRepository,
    });

    const data = JSON.parse(this.component.dataset['landingPage'] || '');

    this.companyId = data?.companyId;
    this.companyType = data?.companyType;
    this.target = new Target();
  }

  computeEventListeners(): IListenerConfig[] {
    const eventListeners: IListenerConfig[] = [];

    const landingPageDependencies = this.dependencies as LandingPageDependencies;
    const eventCallbackMap = {
      'go-to-feature-cloud-security': landingPageDependencies.api.clients.CloudSecurity.goToFeature,
      'open-console-cloud-security': () =>
        landingPageDependencies.api.clients.CloudSecurity.openConsole(this.companyId),
      'go-to-feature-mobile-security': landingPageDependencies.api.clients.MobileSecurity.goToFeature,
      'open-console-mobile-security': () =>
        landingPageDependencies.api.clients.MobileSecurity.openConsole(this.companyId),
      'go-to-feature-email-security': landingPageDependencies.api.clients.EmailSecurity.goToFeature,
      'open-console-email-security': () =>
        landingPageDependencies.api.clients.EmailSecurity.openConsole(this.companyId),
      'go-to-feature-product-trials': landingPageDependencies.api.clients.ProductTrials.goToFeature,
      'go-to-help-and-support': landingPageDependencies.api.clients.HelpAndSupport.goToFeature,
      'go-to-installation-packages': landingPageDependencies.api.clients.InstallationPackages.goToFeature,
      'go-to-early-access': landingPageDependencies.api.clients.EarlyAccess.goToFeature,
      'show-whats-new': landingPageDependencies.api.clients.WhatsNew.openWhatsNew,
      'go-to-feature-msp-product-trials': () => this.redirectToCompaniesHavingMSPInProductTrialsFeatureAvailable(),
      'go-to-trainings': () => this.openMasterclass(),
      'go-to-msp-events': () => this.openEventLinkFromDynamicWidget(),
      'go-to-msp-promo': () => this.openPromoLinkFromDynamicWidget(),
    };

    for (const event in eventCallbackMap) {
      eventListeners.push({
        element: this.view.dom.$landingPageTemplate,
        event,
        callback: async () => {
          try {
            await eventCallbackMap[event]().toPromise();
          } catch (e) {
            showErrorToast(e);
          }
        },
      });
    }

    return eventListeners;
  }

  async getDataForView(): Promise<string | undefined> {
    await this.setupRepobit();
    this.view.setLoadingState();
    this.gzVersion = await this.dataRepository.getGzVersion();
    this.messages = await this.dataRepository.getMessages();
    this.rssFeedUrls = await this.dataRepository.getRssFeedUrls(this.companyId);
    this.dynamicEventWidgetInfo = await this.dataRepository.getDynamicEventWidgetInfo(this.companyId);
    this.dynamicPromoWidgetInfo = await this.dataRepository.getDynamicPromoWidgetInfo(this.companyId);
    this.view.removeLoadingState();
    return this.gzVersion;
  }

  private async setupRepobit(): Promise<void> {
    this.dataRepository.dataProvider.target = this.target;

    try {
      await Launch.load('prod');
    } catch (error) {
      this.target.abort();
    }
  }

  private redirectToCompaniesHavingMSPInProductTrialsFeatureAvailable(): Observable<void> {
    NavigationHelper.navigateTo('/companies');
    setTimeout(this.triggerCompaniesFilteringWithMSPInProductTrialsFeatureAvailable, 300);
    return new Observable<void>();
  }

  /**
   * Apply filtering so that only available companies for msp trial are shown
   *
   * @returns {Promise<void>}
   * @private
   */
  private async triggerCompaniesFilteringWithMSPInProductTrialsFeatureAvailable(): Promise<void> {
    const companiesApp = document.getElementsByTagName(WEB_COMPONENT_COMPANIES_APP)[0] as CompaniesAppWebComponent;
    const checkGridLoaded = setInterval(async () => {
      if (companiesApp.controller !== undefined) {
        const companiesGrid = companiesApp.controller?.view.dom.$companiesGrid as CompaniesGridWebComponent;
        if (companiesGrid.controller !== undefined) {
          const gridView = companiesGrid.controller?.view.dom.$grid as GridViewWebComponent<any, any>;
          if (gridView.gridLoaded) {
            clearInterval(checkGridLoaded);
            const newFilters = FILTERS as Array<any>;
            newFilters.push({
              [ColumnKeys.MSP_IN_PRODUCT_TRIAL_STATUS]: { in: [MspInProductTrialStatus.TRIAL_AVAILABLE] },
            });
            gridView.propertiesSet = false;
            gridView.filters = newFilters;
            gridView.propertiesSet = true;
            await gridView.controller?.handleGridItemsLoad();
          }
        }
      }
    }, 100);
  }

  private openMasterclass(): Observable<void> {
    const masterClassLink = 'https://bitdefender.com/masterclass';
    window.open(masterClassLink, '_blank');
    return new Observable<void>();
  }

  private openEventLinkFromDynamicWidget(): Observable<void> {
    window.open(this.dynamicEventWidgetInfo.buttonLink ?? '', '_blank');
    return new Observable<void>();
  }

  private openPromoLinkFromDynamicWidget(): Observable<void> {
    window.open(this.dynamicPromoWidgetInfo.buttonLink ?? '', '_blank');
    return new Observable<void>();
  }
}
